import React, { useContext, useEffect, useState } from 'react';
import { Linking, View } from 'react-native';
import { Button } from '@rneui/themed';
import Constants from 'expo-constants';
import RenderHTML from 'react-native-render-html';
import WebView from 'react-native-webview';
import IframeRenderer, { iframeModel } from '@native-html/iframe-plugin';
import { parseISO, subMinutes, isBefore, isAfter, isWithinInterval } from 'date-fns';
import PropTypes from 'prop-types';
import { Countdown } from './Countdown';
import ServiceBlock from '../ServiceBlock';
import CdnImageBackground from '../../common/CdnImageBackground';
import ComponentContainer from '../../common/ComponentContainer';
import ComponentTitle from '../../common/ComponentTitle';
import AppConstants from '../../../common/Constants';
import LayoutContext from '../../../contexts/Layout';
import AppContext, { AppActions } from '../../../contexts/App';
import useLiveService from '../../../hooks/useLiveService';
import { fetchDomainItemByKey } from '../../../common/Api';

const renderers = {
    iframe: IframeRenderer
};

const customHTMLElementModels = {
    iframe: iframeModel
};

const LiveService = ({
    backgroundImage,
    buttonLink,
    buttonText,
    countdownText,
    countdownTextColor,
    followContentLink,
    nodeTypeAlias = 'NextLiveService',
    references,
    showServiceBlock = true,
    title = title,
    width,
    hardWidth,
    layoutDimensions
}) => {
    const { layout } = useContext(LayoutContext);
    const {
        state: { action, settings },
        dispatch
    } = useContext(AppContext);
    const nextLiveService = useLiveService();
    const [useAslFeed, setUseAslFeed] = useState(false);
    const [useSpanishFeed, setUseSpanishFeed] = useState(false);
    const [isStarted, setIsStarted] = useState(false);
    const [imageDimensions, setImageDimensions] = useState();

    const renderContent = () => {
        const leadInMinutes = nextLiveService?.nextService.leadInMinutes;
        const dateAndTime = nextLiveService?.nextService.upcomingDateTime;
        const serviceDate = typeof dateAndTime === 'string' ? parseISO(dateAndTime) : new Date(dateAndTime);
        const leadInDate = subMinutes(serviceDate, leadInMinutes);
        const now = new Date();

        if (isBefore(now, serviceDate) && !isAfter(now, leadInDate)) {
            const handlePress = async () => {
                if (followContentLink && references.hasOwnProperty(buttonLink.internalKey)) {
                    followContentLink(buttonLink.isExternal, buttonLink.internalKey, buttonLink.externalUrl);
                } else {
                    if (buttonLink.isExternal) {
                        Linking.openURL(buttonLink.externalUrl);
                    } else {
                        const domain = settings?.websiteSettings?.domain;
                        const page = await fetchDomainItemByKey(domain, buttonLink.internalKey);

                        if (page) {
                            dispatch({
                                data: page.item,
                                href: page.item.rootPath,
                                type: AppActions.NAVIGATE
                            });
                        }
                    }
                }
            };

            return (
                <Countdown
                    actionText={buttonText}
                    imageSrc={backgroundImage}
                    onPress={handlePress}
                    start={dateAndTime}
                    text={countdownText}
                    textColor={countdownTextColor}
                    width={width}
                    hardWidth={hardWidth}
                />
            );
        } else if (isStarted && !action?.href?.equals(AppConstants.REACT_APP_WATCH_ROUTE)) {
            const watch = async () => {
                const response = await window.fetch(`${Constants.manifest.extra.baseUrl}${AppConstants.REACT_APP_PAGE_BASE}${AppConstants.REACT_APP_WATCH_ROUTE}`);
                const json = await response.json();

                if (json) {
                    dispatch({
                        type: AppActions.WATCH_LIVE_SERVICE,
                        data: {
                            page: json.page
                        }
                    });
                }
            };

            return (
                <View style={{ width: width, height: layout.device.isMobile ? 400 : 600 }}>
                    <CdnImageBackground
                        enableOverlay={false}
                        imageDimensions={imageDimensions}
                        setImageDimensions={setImageDimensions}
                        style={{ justifyContent: 'center' }}
                        uri={backgroundImage.cdnImagePath}
                        width={hardWidth}
                    >
                        <Button
                            buttonStyle={{ flexDirection: 'column' }}
                            containerStyle={{ marginHorizontal: 5, marginVertical: 5 }}
                            onPress={async () => await watch()}
                            icon={{
                                color: 'transparent',
                                reverse: true,
                                reverseColor: '#fff',
                                size: 45,
                                name: 'play',
                                type: 'evilicon'
                            }}
                            iconContainerStyle={{ margin: -10 }}
                            title='Watch Now'
                            titleStyle={{ color: '#fff', fontFamily: 'GothamBook', fontSize: layout.device.os.equals('web') ? '20pt' : 20 }}
                            type='clear'
                        />
                    </CdnImageBackground>
                </View>
            );
        } else {
            const buttons = [];
            let src = settings?.vcmsSettings?.livePlayerEnglish;

            if (nextLiveService?.streamsLive) {
                if (isWithinInterval(new Date(), { start: serviceDate, end: leadInDate })) {
                    src = settings?.vcmsSettings?.livePlayerEnglish;
                }
            }

            if (useAslFeed) {
                src = settings?.vcmsSettings?.livePlayerASL;
            } else if (useSpanishFeed) {
                src = settings?.vcmsSettings?.livePlayerSpanish;
            }

            if (nextLiveService?.nextService?.spanishSigner) {
                buttons.push(
                    <Button
                        buttonStyle={{ borderColor: 'white', borderWidth: 1, height: '100%' }}
                        key='spanish-signer'
                        onPress={() => setUseSpanishFeed(!useSpanishFeed)}
                        size='sm'
                        title={useSpanishFeed ? 'EN Switch to English Player' : 'Para escuchar en Español, \noprima Aqui'}
                        titleStyle={{
                            fontFamily: 'GothamBook',
                            fontSize: !layout.device.isMobile ? 16 : 12,
                            color: '#fff'
                        }}
                        type='outline'
                    />
                );
            }

            if (nextLiveService?.nextService?.aslSigner) {
                buttons.push(
                    <Button
                        buttonStyle={{ borderColor: 'white', borderWidth: 1, height: '100%', paddingVertical: 13 }}
                        containerStyle={{ marginLeft: 10 }}
                        key='asl-signer-button'
                        icon={{
                            color: 'white',
                            name: 'american-sign-language-interpreting',
                            size: 16,
                            type: 'font-awesome-5'
                        }}
                        onPress={() => setUseAslFeed(!useAslFeed)}
                        size='sm'
                        title={useAslFeed ? 'Switch to non-ASL Player' : 'Switch to ASL Player'}
                        titleStyle={{
                            color: '#fff',
                            fontFamily: 'GothamBook',
                            fontSize: !layout.device.isMobile ? 16 : 12
                        }}
                        type='outline'
                    />
                );
            }

            return (
                <View style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'center', height: layout.device.isMobile ? 400 : 600, width: width }}>
                        {layout.device.os.equals('web') && (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html:
                                        `<iframe src='${src}' width='100%' height='100%' seamless='seamless'` +
                                        `allowFullScreen='allowFullScreen' title='${nextLiveService?.nextService?.title}'></iframe>`
                                }}
                                style={{ height: '100%', width: '100% ' }}
                            />
                        )}
                        {!layout.device.os.equals('web') && (
                            <RenderHTML
                                baseStyle={{ height: layout.device.isMobile ? 400 : 600 }}
                                // contentWidth={width}
                                renderers={renderers}
                                WebView={WebView}
                                source={{
                                    html:
                                        `<iframe src='${src}' width='100%' height='100%' seamless='seamless'` +
                                        `allowFullScreen='allowFullScreen' title='${nextLiveService?.nextService?.title}'></iframe>`
                                }}
                                customHTMLElementModels={customHTMLElementModels}
                                renderersProps={{
                                    iframe: {
                                        scalesPageToFit: true,
                                        webViewProps: {}
                                    }
                                }}
                            />
                        )}
                    </View>
                    {buttons.length > 0 && (
                        <View
                            style={{
                                width: width,
                                backgroundColor: '#000'
                            }}
                        >
                            <View
                                style={{
                                    alignItems: 'center',
                                    flexDirection: 'row',
                                    justifyContent: layout.device.isMobile ? 'space-around' : 'flex-end',
                                    right: layout.device.isMobile ? undefined : 20,
                                    paddingVertical: 10
                                }}
                            >
                                {buttons.map(button => button)}
                            </View>
                        </View>
                    )}
                </View>
            );
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            const leadInMinutes = nextLiveService?.nextService.leadInMinutes;
            const dateAndTime = nextLiveService?.nextService.upcomingDateTime;
            const serviceDate = typeof dateAndTime === 'string' ? parseISO(dateAndTime) : new Date(dateAndTime);
            const leadInDate = subMinutes(serviceDate, leadInMinutes);
            const now = new Date();

            if (isWithinInterval(now, { start: serviceDate, end: leadInDate }) || isAfter(now, leadInDate)) {
                setIsStarted(true);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [nextLiveService]);

    if (!nextLiveService) {
        return null;
    }

    return (
        <View dataSet={{ umbracoComponentName: nodeTypeAlias }}>
            {!!title && <ComponentTitle title={title} />}
            {renderContent()}
            {showServiceBlock && (
                <ComponentContainer style={{ marginTop: layout.styleGuide.spacer4 }} dataSet={{ spacerTop: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})` }}>
                    <ServiceBlock
                        date={nextLiveService?.nextService?.upcomingDateTime}
                        details={nextLiveService?.nextService?.sermonFullDescription}
                        sermonTitle={nextLiveService?.nextService?.sermonTitle}
                        speaker={nextLiveService?.nextService?.speaker}
                        speakerImage={nextLiveService?.nextService?.speakerPhoto}
                        title={nextLiveService?.nextService?.title}
                    />
                </ComponentContainer>
            )}
        </View>
    );
};

LiveService.propTypes = {
    backgroundImage: PropTypes.shape({
        cdnImagePath: PropTypes.string
    }),
    buttonLink: PropTypes.shape({
        isExternal: PropTypes.bool,
        externalUrl: PropTypes.string,
        internalKey: PropTypes.string
    }),
    buttonText: PropTypes.string,
    countdownText: PropTypes.string,
    followContentLink: PropTypes.func.isRequired,
    title: PropTypes.string,
    references: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    rootPath: PropTypes.string,
    showServiceBlock: PropTypes.bool
};

export default LiveService;
